import type { FabricObject } from 'fabric';
import type {
  CanvasElement,
  ShapeElement,
  PathElement,
  TextElement,
  SvgElement,
  LineElement,
  ImageElement,
} from '../types/index';

export interface MergeCandidate {
  fingerprint: string;
  objects: FabricObject[];
  type: string;
  fill: string;
  stroke: string;
  strokeWidth: number;
  avgWidth: number;
  avgHeight: number;
  count: number;
  suggestedName: string;
}

export interface SmartMergeResult {
  mergedGroups: MergeCandidate[];
  individuals: FabricObject[];
  originalCount: number;
  mergedCount: number;
  strokedCount: number;
  unstrokedCount: number;
}

function generateFingerprint(obj: FabricObject): string {
  const type = obj.type || 'unknown';
  const fill = normalizeFill(obj.fill);
  const stroke = normalizeColor(obj.stroke as string | undefined);
  const strokeWidth = Math.round((obj.strokeWidth || 0) * 10) / 10;
  const width = Math.round(obj.width || 0);
  const height = Math.round(obj.height || 0);

  let pathKey = '';
  if (type === 'path' && (obj as any).path) {
    pathKey = normalizePathData((obj as any).path);
  }

  return `${type}|${fill}|${stroke}|${strokeWidth}|${width}|${height}|${pathKey}`;
}

function normalizeFill(fill: any): string {
  if (!fill) return 'none';
  if (typeof fill === 'string') return fill.toLowerCase().trim();
  if (fill.type) return `gradient_${fill.type}_${JSON.stringify(fill.colorStops || [])}`;
  return String(fill);
}

function normalizeColor(color: string | null | undefined): string {
  if (!color) return 'none';
  return color.toLowerCase().trim();
}

function normalizePathData(pathArray: any[]): string {
  if (!pathArray || !Array.isArray(pathArray)) return '';
  try {
    return pathArray
      .map((segment) => {
        if (!Array.isArray(segment)) return String(segment);
        return segment
          .map((val: any, i: number) => {
            if (i === 0) return val;
            if (typeof val === 'number') return Math.round(val * 10) / 10;
            return val;
          })
          .join(',');
      })
      .join('|');
  } catch {
    return 'unparseable';
  }
}

function generateGroupName(candidate: MergeCandidate): string {
  const count = candidate.count;
  const color = getColorName(candidate.fill);

  let typeName: string;
  switch (candidate.type) {
    case 'circle':
    case 'ellipse':
      typeName = candidate.avgWidth < 10 ? (count > 1 ? 'Dots' : 'Dot') : (count > 1 ? 'Circles' : 'Circle');
      break;
    case 'rect':
      if (candidate.avgWidth < 5 || candidate.avgHeight < 5) {
        typeName = count > 1 ? 'Ticks' : 'Tick';
      } else if (Math.abs(candidate.avgWidth - candidate.avgHeight) < 2) {
        typeName = count > 1 ? 'Squares' : 'Square';
      } else {
        typeName = count > 1 ? 'Rectangles' : 'Rectangle';
      }
      break;
    case 'path':
      if (candidate.fill === 'none' || !candidate.fill) {
        typeName = count > 1 ? 'Strokes' : 'Stroke';
      } else {
        typeName = count > 1 ? 'Paths' : 'Path';
      }
      break;
    case 'line':
      typeName = count > 1 ? 'Lines' : 'Line';
      break;
    case 'polygon':
    case 'polyline':
      typeName = count > 1 ? 'Shapes' : 'Shape';
      break;
    case 'text':
    case 'textbox':
    case 'i-text':
      typeName = count > 1 ? 'Text Elements' : 'Text';
      break;
    default:
      typeName = count > 1 ? 'Elements' : 'Element';
  }

  if (color && color !== 'none' && color !== 'transparent') {
    return `${color} ${typeName} (${count})`;
  }
  return `${typeName} (${count})`;
}

function getColorName(fill: string): string {
  if (!fill || fill === 'none') return '';

  const hex = fill.toLowerCase().replace('#', '');

  const colorMap: Record<string, string> = {
    ffffff: 'White',
    '000000': 'Black',
    ff0000: 'Red',
    '00ff00': 'Green',
    '0000ff': 'Blue',
    ffff00: 'Yellow',
    ff00ff: 'Magenta',
    '00ffff': 'Cyan',
    '00bfff': 'Cyan',
    ffa500: 'Orange',
    '808080': 'Gray',
    c0c0c0: 'Silver',
    a52a2a: 'Brown',
    ffc0cb: 'Pink',
    '800080': 'Purple',
    '008080': 'Teal',
    '000080': 'Navy',
  };

  if (colorMap[hex]) return colorMap[hex];

  if (hex.length === 6) {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    if (Math.abs(r - g) < 20 && Math.abs(g - b) < 20) {
      if (r < 50) return 'Dark';
      if (r < 128) return 'Gray';
      if (r < 200) return 'Light Gray';
      return 'White';
    }

    const max = Math.max(r, g, b);
    if (r === max && r > g + 30 && r > b + 30) return 'Red';
    if (g === max && g > r + 30 && g > b + 30) return 'Green';
    if (b === max && b > r + 30 && b > g + 30) return 'Blue';
    if (r > 200 && g > 100 && b < 80) return 'Orange';
    if (r > 200 && g > 200 && b < 80) return 'Yellow';
    if (r > 150 && b > 150 && g < 100) return 'Purple';
    if (g > 150 && b > 150 && r < 100) return 'Teal';
  }

  return `#${hex.substring(0, 6).toUpperCase()}`;
}

export function analyzeForSmartMerge(
  objects: FabricObject[],
  minGroupSize: number = 2
): SmartMergeResult {
  const fingerprints = new Map<string, FabricObject[]>();

  for (const obj of objects) {
    const fp = generateFingerprint(obj);
    if (!fingerprints.has(fp)) {
      fingerprints.set(fp, []);
    }
    fingerprints.get(fp)!.push(obj);
  }

  const mergedGroups: MergeCandidate[] = [];
  const individuals: FabricObject[] = [];

  for (const [fingerprint, objs] of fingerprints.entries()) {
    if (objs.length >= minGroupSize) {
      const firstObj = objs[0];
      const avgWidth = objs.reduce((sum, o) => sum + (o.width || 0), 0) / objs.length;
      const avgHeight = objs.reduce((sum, o) => sum + (o.height || 0), 0) / objs.length;

      const candidate: MergeCandidate = {
        fingerprint,
        objects: objs,
        type: firstObj.type || 'unknown',
        fill: normalizeFill(firstObj.fill),
        stroke: normalizeColor(firstObj.stroke as string | undefined),
        strokeWidth: firstObj.strokeWidth || 0,
        avgWidth,
        avgHeight,
        count: objs.length,
        suggestedName: '',
      };
      candidate.suggestedName = generateGroupName(candidate);
      mergedGroups.push(candidate);
    } else {
      individuals.push(...objs);
    }
  }

  mergedGroups.sort((a, b) => b.count - a.count);

  let strokedCount = 0;
  let unstrokedCount = 0;
  for (const obj of objects) {
    const raw = (obj as unknown as { stroke?: unknown }).stroke;
    const width = (obj as unknown as { strokeWidth?: unknown }).strokeWidth;
    const hasColor =
      typeof raw === 'string' &&
      raw.length > 0 &&
      raw !== 'none' &&
      raw !== 'transparent';
    const w = typeof width === 'number' ? width : 0;
    if (hasColor && w > 0) strokedCount++;
    else unstrokedCount++;
  }

  return {
    mergedGroups,
    individuals,
    originalCount: objects.length,
    mergedCount: mergedGroups.length + individuals.length,
    strokedCount,
    unstrokedCount,
  };
}

export function mergeCandidateToSvg(candidate: MergeCandidate): {
  svgMarkup: string;
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const objs = candidate.objects;

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const obj of objs) {
    const bound = obj.getBoundingRect();
    minX = Math.min(minX, bound.left);
    minY = Math.min(minY, bound.top);
    maxX = Math.max(maxX, bound.left + bound.width);
    maxY = Math.max(maxY, bound.top + bound.height);
  }

  const groupWidth = Math.max(maxX - minX, 1);
  const groupHeight = Math.max(maxY - minY, 1);

  const svgChildren: string[] = [];

  for (const obj of objs) {
    const svgStr = fabricObjectToSvgFragment(obj, minX, minY);
    if (svgStr) svgChildren.push(svgStr);
  }

  const svgMarkup = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${r2(groupWidth)} ${r2(groupHeight)}" width="${r2(groupWidth)}" height="${r2(groupHeight)}">`,
    ...svgChildren,
    '</svg>',
  ].join('\n');

  return { svgMarkup, x: minX, y: minY, width: groupWidth, height: groupHeight };
}

function fabricObjectToSvgFragment(obj: FabricObject, offsetX: number, offsetY: number): string {
  const objAngle = (obj as any).angle ?? 0;
  if (Math.abs(objAngle) > 0.01 && typeof (obj as any).toSVG === 'function') {
    return `  ${(obj as any).toSVG()}`;
  }

  const fill = typeof obj.fill === 'string' ? obj.fill : 'none';
  const stroke = (obj.stroke as string) || 'none';
  const strokeWidth = obj.strokeWidth || 0;
  const opacity = obj.opacity !== undefined && obj.opacity !== 1 ? ` opacity="${obj.opacity}"` : '';
  const strokeAttr = stroke !== 'none' && strokeWidth > 0 ? ` stroke="${stroke}" stroke-width="${strokeWidth}"` : '';

  const type = obj.type;

  if (type === 'circle') {
    const c = obj as any;
    const cx = r2((c.left || 0) - offsetX + (c.radius || 0));
    const cy = r2((c.top || 0) - offsetY + (c.radius || 0));
    const rad = r2(c.radius || 0);
    return `  <circle cx="${cx}" cy="${cy}" r="${rad}" fill="${fill}"${strokeAttr}${opacity}/>`;
  }

  if (type === 'ellipse') {
    const e = obj as any;
    const cx = r2((e.left || 0) - offsetX + (e.rx || 0));
    const cy = r2((e.top || 0) - offsetY + (e.ry || 0));
    return `  <ellipse cx="${cx}" cy="${cy}" rx="${r2(e.rx || 0)}" ry="${r2(e.ry || 0)}" fill="${fill}"${strokeAttr}${opacity}/>`;
  }

  if (type === 'rect') {
    const rc = obj as any;
    const x = r2((rc.left || 0) - offsetX);
    const y = r2((rc.top || 0) - offsetY);
    const rx = rc.rx ? ` rx="${r2(rc.rx)}"` : '';
    return `  <rect x="${x}" y="${y}" width="${r2(rc.width || 0)}" height="${r2(rc.height || 0)}"${rx} fill="${fill}"${strokeAttr}${opacity}/>`;
  }

  if (type === 'path') {
    const p = obj as any;
    const pathData = p.path ? p.path.map((seg: any[]) => seg.join(' ')).join(' ') : '';
    const tx = r2((p.left || 0) - offsetX - (p.pathOffset?.x || 0));
    const ty = r2((p.top || 0) - offsetY - (p.pathOffset?.y || 0));
    const transform = tx !== 0 || ty !== 0 ? ` transform="translate(${tx},${ty})"` : '';
    return `  <path d="${pathData}" fill="${fill}"${strokeAttr}${opacity}${transform}/>`;
  }

  if (type === 'line') {
    const l = obj as any;
    const x1 = r2((l.x1 || 0) + (l.left || 0) - offsetX);
    const y1 = r2((l.y1 || 0) + (l.top || 0) - offsetY);
    const x2 = r2((l.x2 || 0) + (l.left || 0) - offsetX);
    const y2 = r2((l.y2 || 0) + (l.top || 0) - offsetY);
    return `  <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"${strokeAttr}${opacity}/>`;
  }

  if (type === 'polygon') {
    const pg = obj as any;
    const points = (pg.points || [])
      .map((pt: any) => `${r2(pt.x + (pg.left || 0) - offsetX)},${r2(pt.y + (pg.top || 0) - offsetY)}`)
      .join(' ');
    return `  <polygon points="${points}" fill="${fill}"${strokeAttr}${opacity}/>`;
  }

  if (type === 'polyline') {
    const pl = obj as any;
    const points = (pl.points || [])
      .map((pt: any) => `${r2(pt.x + (pl.left || 0) - offsetX)},${r2(pt.y + (pl.top || 0) - offsetY)}`)
      .join(' ');
    return `  <polyline points="${points}" fill="${fill}"${strokeAttr}${opacity}/>`;
  }

  if (typeof obj.toSVG === 'function') {
    return `  ${obj.toSVG()}`;
  }

  return '';
}

export function elementToSvgString(element: CanvasElement, offsetX: number, offsetY: number): string {
  const x = element.x - offsetX;
  const y = element.y - offsetY;
  const opacity = element.opacity !== undefined && element.opacity !== 1 ? ` opacity="${element.opacity}"` : '';

  switch (element.type) {
    case 'shape': {
      const shape = element as ShapeElement;
      if (shape.shapeType === 'circle') {
        const rx = shape.width / 2;
        const ry = shape.height / 2;
        return `  <ellipse cx="${r2(x + rx)}" cy="${r2(y + ry)}" rx="${r2(rx)}" ry="${r2(ry)}" fill="${shape.fill || 'none'}" stroke="${shape.stroke || 'none'}" stroke-width="${shape.strokeWidth || 0}"${opacity}/>`;
      }
      const rxAttr = shape.borderRadius ? ` rx="${shape.borderRadius}"` : '';
      return `  <rect x="${r2(x)}" y="${r2(y)}" width="${r2(shape.width)}" height="${r2(shape.height)}"${rxAttr} fill="${shape.fill || 'none'}" stroke="${shape.stroke || 'none'}" stroke-width="${shape.strokeWidth || 0}"${opacity}/>`;
    }

    case 'path': {
      const path = element as PathElement;
      const pathData = path.pathData || '';
      const tx = r2(x);
      const ty = r2(y);
      const transform = tx !== 0 || ty !== 0 ? ` transform="translate(${tx},${ty})"` : '';
      return `  <path d="${pathData}" fill="${path.fill || 'none'}" stroke="${path.stroke || 'none'}" stroke-width="${path.strokeWidth || 0}"${opacity}${transform}/>`;
    }

    case 'text': {
      const text = element as TextElement;
      const fontSize = text.fontSize || 16;
      const fontFamily = text.fontFamily || 'sans-serif';
      const fill = text.color || '#ffffff';
      return `  <text x="${r2(x)}" y="${r2(y + fontSize)}" font-family="${fontFamily}" font-size="${fontSize}" fill="${fill}"${opacity}>${escapeXml(text.text || '')}</text>`;
    }

    case 'svg': {
      const svg = element as SvgElement;
      return `  <g transform="translate(${r2(x)},${r2(y)})">${stripSvgWrapper(svg.svgMarkup)}</g>`;
    }

    case 'line': {
      const line = element as LineElement;
      return `  <line x1="${r2(x + (line.x1 || 0))}" y1="${r2(y + (line.y1 || 0))}" x2="${r2(x + (line.x2 || 0))}" y2="${r2(y + (line.y2 || 0))}" stroke="${line.stroke || '#ffffff'}" stroke-width="${line.strokeWidth || 1}"${opacity}/>`;
    }

    case 'image': {
      const img = element as ImageElement;
      return `  <image x="${r2(x)}" y="${r2(y)}" width="${r2(img.width)}" height="${r2(img.height)}" href="${img.src || ''}"${opacity}/>`;
    }

    default:
      return '';
  }
}

export function generateMergedElementName(elements: CanvasElement[]): string {
  const typeCounts = new Map<string, number>();
  for (const el of elements) {
    typeCounts.set(el.type, (typeCounts.get(el.type) || 0) + 1);
  }

  if (typeCounts.size === 1) {
    const [type, count] = [...typeCounts.entries()][0];
    const typeNames: Record<string, string> = {
      shape: 'Shapes',
      path: 'Paths',
      text: 'Text Elements',
      svg: 'SVG Elements',
      image: 'Images',
      line: 'Lines',
      button: 'Buttons',
    };
    return `Merged ${typeNames[type] || 'Elements'} (${count})`;
  }

  return `Merged Elements (${elements.length})`;
}

function escapeXml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function stripSvgWrapper(markup: string): string {
  return markup.replace(/^<svg[^>]*>/, '').replace(/<\/svg>\s*$/, '').trim();
}

function r2(n: number): number {
  return Math.round(n * 100) / 100;
}
